// TODO: migrate this file to a framework-agnostic `common-core` package shared across frontend and backend.

/** Structural type satisfied by both `QuantFileTableData` (frontend) and `QuantDataContext` (backend). */
export type FilterableQuantData = {
  records: (string | null)[][];
  rowNames: (string | null)[][];
  columnNames: (string | null)[][];
  metadata?: {
    arrowColors?: number[][];
    arrowDirection?: string[][];
    isSignificant?: boolean[][];
  };
};

/** Returns true when `dataPath` starts with all segments of `filterPath` (null segments treated as empty). */
export function pathStartsWith(dataPath: (string | null)[], filterPath: string[]): boolean {
  if (filterPath.length > dataPath.length) return false;
  for (let i = 0; i < filterPath.length; i++) {
    if ((dataPath[i] ?? '') !== filterPath[i]) return false;
  }
  return true;
}

export const QUANT_PATH_SEPARATOR = ' > ';

/** Converts a nested name matrix into a deduplicated array of `"A > B"` path strings, stripping nulls and empty segments. */
export function formatQuantDataPaths(names: (string | null)[][]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const path of names) {
    const cleaned = path.filter((s): s is string => s !== null && s.trim() !== '');
    if (cleaned.length === 0) continue;
    const key = cleaned.join(QUANT_PATH_SEPARATOR);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(key);
  }

  return result;
}

/** Returns a filtered copy of `data` keeping only rows/columns whose paths start with at least one filter path. */
export function filterQuantData<T extends FilterableQuantData>(
  data: T,
  rowFilters: string[][],
  columnFilters: string[][]
): T {
  if (rowFilters.length === 0 && columnFilters.length === 0) return data;

  const keepRow = (i: number): boolean => {
    const path = data.rowNames[i];
    if (!path) return false;
    return rowFilters.some((fp) => pathStartsWith(path, fp));
  };

  const keepCol = (i: number): boolean => {
    const path = data.columnNames[i];
    if (!path || path.length === 0) return false;
    return columnFilters.some((fp) => pathStartsWith(path, fp));
  };

  const rowSet = rowFilters.length > 0 ? new Set(data.rowNames.map((_, i) => i).filter(keepRow)) : null;

  const colSet = columnFilters.length > 0 ? new Set(data.columnNames.map((_, i) => i).filter(keepCol)) : null;

  const filterMatrix = <V>(matrix: V[][] | undefined): V[][] | undefined => {
    if (!matrix) return undefined;
    return matrix
      .map((row, ri) => {
        if (rowSet && !rowSet.has(ri)) return null;
        return colSet ? row.filter((_, ci) => colSet.has(ci)) : row;
      })
      .filter((row) => row !== null);
  };

  const filteredRecords = filterMatrix(data.records) ?? [];

  const filteredMetadata =
    data.metadata && (rowSet ?? colSet)
      ? {
          arrowColors: filterMatrix(data.metadata.arrowColors),
          arrowDirection: filterMatrix(data.metadata.arrowDirection),
          isSignificant: filterMatrix(data.metadata.isSignificant),
        }
      : data.metadata;

  return {
    ...data,
    rowNames: rowSet ? data.rowNames.filter((_, i) => rowSet.has(i)) : data.rowNames,
    columnNames: colSet ? data.columnNames.filter((_, i) => colSet.has(i)) : data.columnNames,
    records: filteredRecords,
    metadata: filteredMetadata,
  };
}
